<template>
  <div class="profile">
    <h1>个人中心</h1>
    <div class="profile-card">
      <div class="profile-main">
        <div class="profile-info">
          <h3>基本信息</h3>
          <div class="info-item">
            <label>用户名</label>
            <span>{{ userInfo.username }}</span>
          </div>
          <div class="info-item">
            <label>邮箱</label>
            <span>{{ userInfo.email }}</span>
          </div>
          <div class="info-item">
            <label>剩余API次数</label>
            <div class="api-container">
              <span>{{ userInfo.remaining_apis }}</span>
              <button @click="showProductModal" class="buy-btn">
                <i class="fas fa-shopping-cart"></i> 购买
              </button>
              <button @click="showKeyModal" class="key-btn">
                <i class="fas fa-key"></i> 密钥兑换
              </button>
            </div>
          </div>
          <div class="info-item">
            <label>题库配置</label>
            <div class="config-container">
              <button @click="openConfigModal" class="config-btn">
                <i class="fas fa-cog"></i> 查看配置
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="adImages.length"
          class="profile-ad"
          @mouseenter="stopAdAutoplay"
          @mouseleave="startAdAutoplay"
        >
          <div class="ad-header">
            <h3>广告推荐</h3>
          </div>
          <div class="ad-carousel">
            <div
              class="ad-track"
              :style="{ transform: `translateX(-${activeAdIndex * 100}%)` }"
            >
              <div
                v-for="(ad, index) in adImages"
                :key="index"
                class="ad-slide"
                :class="{ clickable: ad.showWechatPopup || ad.clickUrl }"
                @click="handleAdClick(ad)"
              >
                <img :src="ad.src" :alt="ad.alt" class="ad-image">
              </div>
            </div>

            <button
              v-if="adImages.length > 1"
              class="ad-nav ad-nav-prev"
              @click="prevAd"
              aria-label="上一张广告"
            >
              <i class="fas fa-chevron-left"></i>
            </button>
            <button
              v-if="adImages.length > 1"
              class="ad-nav ad-nav-next"
              @click="nextAd"
              aria-label="下一张广告"
            >
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>

          <div v-if="adImages.length > 1" class="ad-dots">
            <button
              v-for="(ad, index) in adImages"
              :key="ad.alt"
              class="ad-dot"
              :class="{ active: activeAdIndex === index }"
              :aria-label="`切换到第${index + 1}张广告`"
              @click="goToAd(index)"
            ></button>
          </div>
        </div>
      </div>
    </div>

    <!-- 签到卡片 -->
    <div class="checkin-card">
      <div class="checkin-header">
        <h3>每日签到</h3>
        <div class="checkin-summary">
          <button 
            class="checkin-btn" 
            :class="{ 'checked': checkinMonthly.checked_today, 'loading': isCheckingIn }"
            :disabled="checkinMonthly.checked_today || isCheckingIn"
            @click="doCheckIn"
          >
            <template v-if="isCheckingIn">
              <span class="checkin-spinner"></span> 签到中...
            </template>
            <template v-else-if="checkinMonthly.checked_today">
              <i class="fas fa-check-circle"></i> 今日已签到
            </template>
            <template v-else>
              <i class="fas fa-calendar-check"></i> 立即签到
            </template>
          </button>
          <span class="summary-item">本月签到 <strong>{{ checkinMonthly.total_days }}</strong> 天</span>
          <span class="summary-item">累计奖励 <strong>{{ checkinMonthly.total_reward }}</strong> 次</span>
        </div>
      </div>
      <div class="checkin-body">
        <div class="checkin-calendar">
          <div class="calendar-nav">
            <button class="nav-btn" @click="changeMonth(-1)"><i class="fas fa-chevron-left"></i></button>
            <span class="calendar-title">{{ calendarYear }}年{{ calendarMonth }}月</span>
            <button class="nav-btn" @click="changeMonth(1)"><i class="fas fa-chevron-right"></i></button>
          </div>
          <div class="calendar-grid">
            <div class="calendar-weekday" v-for="day in ['日','一','二','三','四','五','六']" :key="day">{{ day }}</div>
            <div 
              v-for="(cell, idx) in calendarCells" 
              :key="idx"
              class="calendar-cell"
              :class="{ 
                'empty': !cell.day, 
                'checked': cell.checked,
                'today': cell.isToday
              }"
            >
              <span v-if="cell.day" class="cell-day">{{ cell.day }}</span>
              <span v-if="cell.reward" class="cell-reward">+{{ cell.reward }}</span>
            </div>
          </div>
        </div>
      </div>
      <div v-if="checkinMessage" class="checkin-message" :class="checkinMessageType">
        {{ checkinMessage }}
      </div>
    </div>

    <!-- 商品弹窗 -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>购买API次数</h3>
          <button class="close-btn" @click="closeModal">&times;</button>
        </div>
        <div class="modal-body">
          <div v-if="isLoading" class="loading">
            <div class="spinner"></div>
            <p>加载中...</p>
          </div>
          <div v-else-if="error" class="error-message">
            {{ error }}
            <button @click="fetchProducts" class="retry-btn">重试</button>
          </div>
          <div v-else class="product-list">
            <div 
              v-for="product in products" 
              :key="product.id" 
              class="product-item"
              :class="{ 'selected': selectedProduct === product.id }"
              @click="selectProduct(product.id)"
            >
              <div class="product-info">
                <h4>{{ product.name }}</h4>
                <div class="product-details">
                  <span class="product-count">{{ product.count }} 次</span>
                  <span class="product-price">¥{{ product.price }}</span>
                </div>
              </div>
              <div class="product-check" v-if="selectedProduct === product.id">
                <i class="fas fa-check"></i>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <div class="pay-type-selector">
            <button
              class="pay-type-btn"
              :class="{ active: selectedPayType === 'alipay' }"
              @click="selectedPayType = 'alipay'"
            >
              <svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" width="18" height="18"><path d="M1024.0512 701.0304V196.864A196.9664 196.9664 0 0 0 827.136 0H196.864A196.9664 196.9664 0 0 0 0 196.864v630.272A196.9152 196.9152 0 0 0 196.864 1024h630.272a197.12 197.12 0 0 0 193.8432-162.0992c-52.224-22.6304-278.528-120.32-396.4416-176.64-89.7024 108.6976-183.7056 173.9264-325.3248 173.9264s-236.1856-87.2448-224.8192-194.048c7.4752-70.0416 55.552-184.576 264.2944-164.9664 110.08 10.3424 160.4096 30.8736 250.1632 60.5184 23.1936-42.5984 42.496-89.4464 57.1392-139.264H248.064v-39.424h196.9152V311.1424H204.8V267.776h240.128V165.632s2.1504-15.9744 19.8144-15.9744h98.4576V267.776h256v43.4176h-256V381.952h208.8448a805.9904 805.9904 0 0 1-84.8384 212.6848c60.672 22.016 336.7936 106.3936 336.7936 106.3936zM283.5456 791.6032c-149.6576 0-173.312-94.464-165.376-133.9392 7.8336-39.3216 51.2-90.624 134.4-90.624 95.5904 0 181.248 24.4736 284.0576 74.5472-72.192 94.0032-160.9216 150.016-253.0816 150.016z" fill="#009FE8"/></svg>
              支付宝(仅限手机)
            </button>
            <button
              class="pay-type-btn"
              :class="{ active: selectedPayType === 'wxpay' }"
              @click="selectedPayType = 'wxpay'"
            >
              <svg viewBox="0 0 1228 1024" xmlns="http://www.w3.org/2000/svg" width="18" height="18"><path d="M530.8928 703.1296a41.472 41.472 0 0 1-35.7376-19.8144l-2.7136-5.5808L278.272 394.752a18.7392 18.7392 0 0 1-2.048-8.1408 19.968 19.968 0 0 1 20.48-19.3536c4.608 0 8.8576 1.4336 12.288 3.84l234.3936 139.9296a64.4096 64.4096 0 0 0 54.528 5.9392L1116.2624 204.8C1004.9536 80.896 821.76 0 614.4 0 275.0464 0 0 216.576 0 483.6352c0 145.7152 82.7392 276.8896 212.2752 365.5168a38.1952 38.1952 0 0 1 17.2032 31.488 44.4928 44.4928 0 0 1-2.1504 12.3904l-27.6992 97.4848c-1.3312 4.608-3.328 9.3696-3.328 14.1312 0 10.752 9.216 19.3536 20.48 19.3536 4.4032 0 8.0384-1.536 11.776-3.584l134.5536-73.3184c10.1376-5.5296 20.7872-8.96 32.6144-8.96 6.2976 0 12.288 0.9216 18.0736 2.5088 62.72 17.0496 130.4576 26.5728 200.5504 26.5728C953.7024 967.168 1228.8 750.592 1228.8 483.6352c0-80.9472-25.4464-157.1328-70.0416-224.1024l-604.9792 436.992-4.4544 2.4064a42.1376 42.1376 0 0 1-18.432 4.1984z" fill="#15BA11"/></svg>
              微信
            </button>
          </div>
          <div class="footer-actions">
            <button class="cancel-btn" @click="closeModal">取消</button>
            <button
              class="confirm-btn"
              :disabled="!selectedProduct || isProcessing"
              @click="purchaseProduct"
            >
              {{ isProcessing ? '处理中...' : '确认购买' }}
            </button>
            <button class="unable-buy-btn" @click="showWechatQR">无法购买？</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 密钥兑换弹窗 -->
    <div v-if="showKeyExchangeModal" class="modal-overlay" @click="closeKeyModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>密钥兑换</h3>
          <button class="close-btn" @click="closeKeyModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="key-exchange-form">
            <div class="form-group">
              <label for="keyCode">密钥代码</label>
              <input 
                id="keyCode"
                v-model="keyCode" 
                type="text" 
                placeholder="请输入密钥代码"
                class="key-input"
              >
            </div>
            <p class="key-tip">
              <i class="fas fa-info-circle"></i>
              输入有效的密钥代码可以增加您的API查询次数
            </p>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeKeyModal">取消</button>
          <button 
            class="confirm-btn" 
            :disabled="!keyCode.trim() || isUsingKey" 
            @click="useKey"
          >
            {{ isUsingKey ? '兑换中...' : '确认兑换' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 题库配置弹窗 -->
    <div v-if="showConfigModal" class="modal-overlay" @click="closeConfigModal">
      <div class="modal-content config-modal" @click.stop>
        <div class="modal-header">
          <h3>题库配置</h3>
          <button class="close-btn" @click="closeConfigModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="config-content">
            <div class="config-actions">
              <button @click="refreshTkToken" class="refresh-btn" :disabled="isRefreshing">
                {{ isRefreshing ? '刷新中...' : '刷新 TK Token' }}
              </button>
              <button @click="copyConfig" class="copy-btn">
                <i class="fas fa-copy"></i> 复制配置
              </button>
            </div>
            <div class="config-code">
              <pre><code class="language-json" v-html="highlightedTikuConfig"></code></pre>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeConfigModal">关闭</button>
        </div>
      </div>
    </div>
    
    <!-- 支付二维码弹窗 -->
    <div v-if="showPaymentModal && paymentData" class="modal-overlay" @click="closePaymentModal">
      <div class="modal-content payment-modal" @click.stop>
        <div class="modal-header">
          <h3>扫码支付</h3>
          <button class="close-btn" @click="closePaymentModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="payment-content">
            <div class="payment-info">
              <h4>{{ paymentData.product_name }}</h4>
              <p class="payment-details">
                <span class="api-count">{{ paymentData.api_count }} 次API调用</span>
                <span class="payment-amount">¥{{ paymentData.amount }}</span>
              </p>
              <p class="order-no">订单号：{{ paymentData.out_trade_no }}</p>
            </div>
            <div class="qr-payment-container">
              <p class="payment-tip">请使用微信扫描下方二维码完成支付</p>
              <div class="payment-qr-container">
                <img v-if="paymentData.qr_code" :src="paymentData.qr_code" alt="支付二维码" class="payment-qr">
                <a v-else-if="paymentData.pay_url" :href="paymentData.pay_url" target="_blank" class="pay-url-btn">点击前往支付</a>
              </div>
              <p class="payment-note">支付完成后页面将自动刷新</p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closePaymentModal">取消支付</button>
          <button class="confirm-btn" @click="refreshAfterPayment">已完成支付</button>
        </div>
      </div>
    </div>
    
    <!-- 微信二维码弹窗 -->
    <div v-if="showWechatModal" class="modal-overlay" @click="closeWechatModal">
      <div class="modal-content wechat-modal" @click.stop>
        <div class="modal-header">
          <h3>添加客服微信</h3>
          <button class="close-btn" @click="closeWechatModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="wechat-content">
            <p class="wechat-tip">扫描下方二维码添加客服微信，解决您的购买问题</p>
            <div class="qr-container">
              <img src="../assets/anqingyou.jpg" alt="客服微信二维码" class="wechat-qr">
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeWechatModal">关闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';

export default {
  name: 'ProfilePage',
  data() {
    return {
      userInfo: {
        username: '',
        email: '',
        remaining_apis: 0,
        created_at: null,
        updated_at: null
      },
      tktoken: '',
      isRefreshing: false,
      showModal: false,
      products: [],
      selectedProduct: null,
      selectedPayType: 'alipay',
      isLoading: false,
      isProcessing: false,
      error: null,
      
      // 密钥兑换相关
      showKeyExchangeModal: false,
      keyCode: '',
      isUsingKey: false,
      
      // 题库配置相关
      showConfigModal: false,
      
      // 微信二维码相关
      showWechatModal: false,
      
      // 支付订单相关
      showPaymentModal: false,
      paymentData: null,

      // 签到相关
      isCheckingIn: false,
      checkinMessage: null,
      checkinMessageType: 'success',
      calendarYear: new Date().getFullYear(),
      calendarMonth: new Date().getMonth() + 1,
      checkinMonthly: {
        checked_today: false,
        total_days: 0,
        total_reward: 0,
        days: []
      },
      adImages: [
        {
          src: '/ads/微信图片_20260518230149_compressed.png',
          alt: '广告图 1',
          showWechatPopup: false
        },
        {
          src: '/ads/微信图片_20260518230138_compressed.png',
          alt: '广告图 2',
          showWechatPopup: false,
          clickUrl: 'https://admin.aceee.de/?id=10108'
        },
        {
          src: '/ads/微信图片_20260518230155_compressed.png',
          alt: '广告图 3',
          showWechatPopup: true
        }
      ],
      activeAdIndex: 0,
      adAutoplayTimer: null
    }
  },
  computed: {
    calendarCells() {
      const year = this.calendarYear;
      const month = this.calendarMonth;
      const firstDay = new Date(year, month - 1, 1).getDay();
      const daysInMonth = new Date(year, month, 0).getDate();
      const today = new Date();
      const isCurrentMonth = today.getFullYear() === year && today.getMonth() + 1 === month;

      const checkedMap = {};
      for (const d of this.checkinMonthly.days) {
        const dayNum = parseInt(d.check_date.split('-')[2], 10);
        checkedMap[dayNum] = d.reward;
      }

      const cells = [];
      for (let i = 0; i < firstDay; i++) {
        cells.push({ day: null });
      }
      for (let d = 1; d <= daysInMonth; d++) {
        cells.push({
          day: d,
          checked: !!checkedMap[d],
          reward: checkedMap[d] || null,
          isToday: isCurrentMonth && today.getDate() === d
        });
      }
      return cells;
    },
    tikuConfig() {
      const tktoken = this.userInfo.tktoken || '${token}';
      return `[
  {
    "name": "知弈题库",
    "homepage": "https://anqingyou.top/",
    "url": "${API_BASE_URL}/question/query",
    "method": "get",
    "type": "GM_xmlhttpRequest",
    "data": {
      "q": "\${title}",
      "type": "\${type}",
      "options": "\${options}",
      "tktoken": "${tktoken}"

    },
     "handler": "return (res)=>res.data.code === 0 ? [res.data.msg, undefined] : [res.data.msg,res.data.data,{ai: res.data.ai}]"
  }

]`
    },
    highlightedTikuConfig() {
      return this.highlightJson(this.tikuConfig);
    }
  },
  created() {
    this.fetchUserInfo();
    this.fetchCheckinMonthly();
  },
  mounted() {
    this.startAdAutoplay();
  },
  beforeUnmount() {
    this.stopAdAutoplay();
  },
  methods: {
    async fetchUserInfo() {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }

        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.USER.INFO}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.status === 401) {
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          this.$router.push('/login');
          return;
        }

        if (!response.ok) {
          throw new Error('获取用户信息失败');
        }

        const data = await response.json();
        this.userInfo = data.user;
        localStorage.setItem('user', JSON.stringify(this.userInfo));
      } catch (error) {
        console.error('获取用户信息失败:', error);
      }
    },
    formatDate(dateString) {
      if (!dateString) return '暂无数据';
      const date = new Date(dateString);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    async refreshTkToken() {
      try {
        this.isRefreshing = true;
        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }

        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.USER.REFRESH_TKTOKEN}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.status === 401) {
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          this.$router.push('/login');
          return;
        }

        if (!response.ok) {
          throw new Error('刷新TK Token失败');
        }

        const data = await response.json();
        this.userInfo.tktoken = data.tktoken;
      } catch (error) {
        console.error('刷新TK Token失败:', error);
      } finally {
        this.isRefreshing = false;
      }
    },

    // 显示商品弹窗
    showProductModal() {
      this.showModal = true;
      this.fetchProducts();
    },

    // 显示密钥兑换弹窗
    showKeyModal() {
      this.showKeyExchangeModal = true;
      this.keyCode = '';
    },

    // 关闭弹窗
    closeModal() {
      this.showModal = false;
      this.selectedProduct = null;
      this.error = null;
    },

    // 关闭密钥兑换弹窗
    closeKeyModal() {
      this.showKeyExchangeModal = false;
      this.keyCode = '';
      this.error = null;
    },

    // 显示题库配置弹窗
    openConfigModal() {
      this.showConfigModal = true;
    },

    // 关闭题库配置弹窗
    closeConfigModal() {
      this.showConfigModal = false;
    },
    
    // 显示微信二维码弹窗
    showWechatQR() {
      this.showWechatModal = true;
    },

    handleAdClick(ad) {
      if (ad.clickUrl) {
        window.open(ad.clickUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      if (!ad.showWechatPopup) return;
      this.showWechatQR();
    },
    
    // 关闭微信二维码弹窗
    closeWechatModal() {
      this.showWechatModal = false;
    },
    
    // 关闭支付二维码弹窗
    closePaymentModal() {
      this.showPaymentModal = false;
      this.paymentData = null;
    },
    
    // 支付完成后刷新用户信息
    async refreshAfterPayment() {
      this.closePaymentModal();
      await this.fetchUserInfo();
      alert('支付状态已更新，请查看您的API次数');
    },

    // 复制配置到剪贴板
    copyConfig() {
      navigator.clipboard.writeText(this.tikuConfig).then(() => {
        alert('配置已复制到剪贴板');
      }).catch(err => {
        console.error('复制失败:', err);
        alert('复制失败，请手动复制');
      });
    },

    escapeHtml(text) {
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    },

    highlightJson(jsonText) {
      const escapedText = this.escapeHtml(jsonText);
      return escapedText.replace(
        /("(\\u[\da-fA-F]{4}|\\[^u]|[^\\"])*"(\s*:)?|\btrue\b|\bfalse\b|\bnull\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g,
        (match) => {
          let tokenClass = 'json-number';

          if (match.startsWith('"')) {
            tokenClass = match.endsWith(':') ? 'json-key' : 'json-string';
          } else if (match === 'true' || match === 'false') {
            tokenClass = 'json-boolean';
          } else if (match === 'null') {
            tokenClass = 'json-null';
          }

          return `<span class="${tokenClass}">${match}</span>`;
        }
      );
    },

    startAdAutoplay() {
      if (this.adAutoplayTimer || this.adImages.length < 2) return;
      this.adAutoplayTimer = setInterval(() => {
        this.nextAd();
      }, 4000);
    },

    stopAdAutoplay() {
      if (!this.adAutoplayTimer) return;
      clearInterval(this.adAutoplayTimer);
      this.adAutoplayTimer = null;
    },

    nextAd() {
      this.activeAdIndex = (this.activeAdIndex + 1) % this.adImages.length;
    },

    prevAd() {
      this.activeAdIndex = (this.activeAdIndex - 1 + this.adImages.length) % this.adImages.length;
    },

    goToAd(index) {
      this.activeAdIndex = index;
    },

    // 获取商品列表
    async fetchProducts() {
      this.isLoading = true;
      this.error = null;
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }

        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.USER.PRODUCTS}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.status === 401) {
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          this.$router.push('/login');
          return;
        }

        if (!response.ok) {
          throw new Error('获取商品列表失败');
        }

        const data = await response.json();
        if (data.code === 1) {
          this.products = data.data;
        } else {
          throw new Error(data.message || '获取商品列表失败');
        }
      } catch (error) {
        console.error('获取商品列表失败:', error);
        this.error = error.message || '获取商品列表失败，请稍后再试';
      } finally {
        this.isLoading = false;
      }
    },

    // 选择商品
    selectProduct(productId) {
      this.selectedProduct = productId;
    },

    // 购买商品
    async purchaseProduct() {
      if (!this.selectedProduct) return;
      
      this.isProcessing = true;
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }

        // 调用创建支付订单API
        await this.createPayment(this.selectedProduct, this.selectedPayType);
        this.closeModal();
      } catch (error) {
        console.error('购买失败:', error);
        alert('购买失败: ' + (error.message || '未知错误'));
      } finally {
        this.isProcessing = false;
      }
    },
    
    // 创建支付订单 
    async createPayment(productId, payType) {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.USER.CREATE_PAYMENT_ANQING}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + localStorage.getItem('token')
        },
        body: JSON.stringify({
          product_id: productId,
          pay_type: payType
        })
      }); 
      
      if (response.status === 401) {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        this.$router.push('/login');
        return;
      }
      
      const result = await response.json();
      if (result.code === 1) {
        const url = result.data.pay_url || result.data.qr_code;
        if (url) {
          window.location.href = url;
        } else {
          alert('获取支付链接失败');
        }
      } else {
        alert(result.message);
      } 
    },

    // 使用密钥兑换
    async useKey() {
      if (!this.keyCode.trim()) {
        alert('请输入密钥代码');
        return;
      }

      try {
        this.isUsingKey = true;
        
        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }

        const response = await fetch(`${API_BASE_URL}/user/use-key`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            key_code: this.keyCode.trim()
          })
        });

        if (response.status === 401) {
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          this.$router.push('/login');
          return;
        }

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || '使用密钥失败');
        }

        const data = await response.json();
        alert(`密钥使用成功！增加了 ${data.added_count} 次查询次数`);
        
        // 刷新用户信息
        await this.fetchUserInfo();
        this.closeKeyModal();
      } catch (error) {
        console.error('使用密钥失败:', error);
        alert('使用密钥失败: ' + error.message);
      } finally {
        this.isUsingKey = false;
      }
    },

    async doCheckIn() {
      this.isCheckingIn = true;
      this.checkinMessage = null;
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }

        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.USER.CHECKIN}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.status === 401) {
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          this.$router.push('/login');
          return;
        }

        const result = await response.json();
        if (result.code === 1) {
          this.checkinMessage = `签到成功！获得 ${result.data.reward} 次API调用，当前剩余 ${result.data.remaining_apis} 次`;
          this.checkinMessageType = 'success';
          await this.fetchCheckinMonthly();
          await this.fetchUserInfo();
        } else {
          this.checkinMessage = result.message || '签到失败';
          this.checkinMessageType = 'error';
        }
      } catch (error) {
        console.error('签到失败:', error);
        this.checkinMessage = '签到失败，请稍后重试';
        this.checkinMessageType = 'error';
      } finally {
        this.isCheckingIn = false;
      }
    },

    async fetchCheckinMonthly() {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const params = new URLSearchParams({
          year: this.calendarYear.toString(),
          month: this.calendarMonth.toString()
        });

        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.USER.CHECKIN_MONTHLY}?${params}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.status === 401) {
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          this.$router.push('/login');
          return;
        }

        if (!response.ok) return;

        const result = await response.json();
        if (result.code === 1) {
          this.checkinMonthly = result.data;
        }
      } catch (error) {
        console.error('获取签到信息失败:', error);
      }
    },

    changeMonth(delta) {
      this.calendarMonth += delta;
      if (this.calendarMonth > 12) {
        this.calendarMonth = 1;
        this.calendarYear++;
      } else if (this.calendarMonth < 1) {
        this.calendarMonth = 12;
        this.calendarYear--;
      }
      this.fetchCheckinMonthly();
    }
  }
}
</script>

<style scoped>
.profile {
  padding: 20px;
}

.profile-card {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.profile-main {
  display: flex;
  width: 100%;
  gap: 24px;
  align-items: stretch;
}

.profile-info {
  flex: 1;
  min-width: 0;
}

.profile-ad {
  width: 560px;
  max-width: 48%;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}

.ad-header {
  margin-bottom: 12px;
}

.ad-header h3 {
  margin: 0;
  color: #333;
}

.ad-carousel {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 14px;
  background: #f3f4f6;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  aspect-ratio: 1444 / 527;
}

.ad-track {
  display: flex;
  width: 100%;
  height: 100%;
  transition: transform 0.45s ease;
}

.ad-slide {
  flex: 0 0 100%;
  width: 100%;
  height: 100%;
}

.ad-slide.clickable {
  cursor: pointer;
}

.ad-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.ad-slide.clickable .ad-image {
  transition: transform 0.25s ease, filter 0.25s ease;
}

.ad-slide.clickable:hover .ad-image {
  transform: scale(1.01);
  filter: brightness(0.96);
}

.ad-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.55);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
}

.ad-nav:hover {
  background: rgba(15, 23, 42, 0.75);
}

.ad-nav-prev {
  left: 12px;
}

.ad-nav-next {
  right: 12px;
}

.ad-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
}

.ad-dot {
  width: 10px;
  height: 10px;
  border: none;
  border-radius: 50%;
  background: #cbd5e1;
  cursor: pointer;
  padding: 0;
  transition: all 0.2s ease;
}

.ad-dot.active {
  width: 26px;
  border-radius: 999px;
  background: #3b82f6;
}

.info-item {
  display: flex;
  margin: 15px 0;
}

.info-item label {
  width: 100px;
  color: #666;
}

.api-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.buy-btn {
  padding: 6px 12px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: background-color 0.3s;
}

.buy-btn:hover {
  background-color: #388E3C;
}

.key-btn {
  padding: 6px 12px;
  background-color: #FF9800;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: background-color 0.3s;
}

.key-btn:hover {
  background-color: #F57C00;
}

.config-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.config-btn {
  padding: 6px 12px;
  background-color: #9C27B0;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: background-color 0.3s;
}

.config-btn:hover {
  background-color: #7B1FA2;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 15px;
}

.stat-item {
  text-align: center;
  padding: 15px;
  background: #f5f6fa;
  border-radius: 4px;
}

.stat-label {
  color: #666;
  margin-bottom: 5px;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #2196F3;
}

.tktoken-container {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.tktoken-value {
  flex: 1;
  word-break: break-all;
}

.refresh-btn {
  padding: 6px 12px;
  background-color: #2196F3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  min-width: 80px;
}

.refresh-btn:hover {
  background-color: #1976D2;
}

.refresh-btn:disabled {
  background-color: #BDBDBD;
  cursor: not-allowed;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  animation: modal-appear 0.3s ease-out;
}

@keyframes modal-appear {
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
}

.modal-body {
  padding: 20px;
  max-height: 400px;
  overflow-y: auto;
}

.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.cancel-btn, .confirm-btn, .retry-btn {
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  border: none;
}

.cancel-btn {
  background-color: #f1f1f1;
  color: #333;
}

.confirm-btn {
  background-color: #2196F3;
  color: white;
}

.confirm-btn:disabled {
  background-color: #BDBDBD;
  cursor: not-allowed;
}

.unable-buy-btn {
  background-color: transparent;
  color: #FF5722;
  border: 1px solid #FF5722;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.unable-buy-btn:hover {
  background-color: rgba(255, 87, 34, 0.1);
}

.modal-footer {
  flex-direction: column;
  align-items: stretch;
}

.pay-type-selector {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

.pay-type-btn {
  flex: 1;
  padding: 8px 0;
  border-radius: 4px;
  border: 1px solid #ddd;
  background: #f9f9f9;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.pay-type-btn.active {
  border-color: #2196F3;
  background: #e3f2fd;
  color: #2196F3;
  font-weight: 600;
}

.footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.retry-btn {
  background-color: #2196F3;
  color: white;
  margin-top: 10px;
}

/* 商品列表样式 */
.product-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.product-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border: 2px solid #eee;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.product-item:hover {
  border-color: #2196F3;
  background-color: #f9f9f9;
}

.product-item.selected {
  border-color: #2196F3;
  background-color: #E3F2FD;
}

.product-info {
  flex: 1;
}

.product-info h4 {
  margin: 0 0 10px 0;
  color: #333;
}

.product-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.product-count {
  color: #666;
  font-size: 14px;
}

.product-price {
  color: #E91E63;
  font-weight: bold;
  font-size: 18px;
}

.product-check {
  color: #2196F3;
  font-size: 20px;
}

/* 加载状态 */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 0;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: #2196F3;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-message {
  color: #D32F2F;
  text-align: center;
  padding: 20px 0;
}

/* 支付二维码弹窗样式 */
.payment-modal {
  max-width: 500px;
}

.payment-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.payment-info {
  text-align: center;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.payment-info h4 {
  margin: 0 0 10px 0;
  color: #333;
  font-size: 18px;
}

.payment-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 10px 0;
}

.api-count {
  color: #666;
  font-size: 14px;
}

.payment-amount {
  color: #E91E63;
  font-weight: bold;
  font-size: 20px;
}

.order-no {
  color: #888;
  font-size: 12px;
  margin: 5px 0 0 0;
}

.qr-payment-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.payment-tip {
  text-align: center;
  margin-bottom: 15px;
  color: #555;
  font-weight: 500;
}

.payment-qr-container {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-bottom: 15px;
}

.payment-qr {
  width: 200px;
  height: 200px;
  object-fit: contain;
  border: 2px solid #eee;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.payment-note {
  text-align: center;
  color: #888;
  font-size: 12px;
  margin: 0;
}

/* 微信二维码弹窗样式 */
.wechat-modal {
  max-width: 400px;
}

.wechat-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 0;
}

.wechat-tip {
  text-align: center;
  margin-bottom: 20px;
  color: #555;
}

.qr-container {
  display: flex;
  justify-content: center;
  width: 100%;
}

.wechat-qr {
  width: 200px;
  height: 200px;
  object-fit: contain;
  border: 1px solid #eee;
  border-radius: 4px;
}

/* 密钥兑换表单样式 */
.key-exchange-form {
  padding: 10px 0;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.key-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.3s;
  box-sizing: border-box;
}

.key-input:focus {
  outline: none;
  border-color: #2196F3;
  box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
}

.key-tip {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 14px;
  margin: 15px 0 0 0;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border-left: 4px solid #2196F3;
}

.key-tip i {
  color: #2196F3;
}

/* 题库配置弹窗样式 */
.config-modal {
  max-width: 800px;
  width: 95%;
}

.config-content {
  padding: 0;
}

.config-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 15px;
}

.copy-btn {
  padding: 8px 16px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: background-color 0.3s;
}

.copy-btn:hover {
  background-color: #388E3C;
}

.config-code {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  overflow: hidden;
}

.config-code pre {
  margin: 0;
  padding: 20px;
  overflow-x: auto;
  background-color: #1f2937;
  color: #e2e8f0;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.5;
}

.config-code code {
  background: none;
  padding: 0;
  border-radius: 0;
  font-family: inherit;
  display: block;
  white-space: pre;
}

.language-json {
  color: #e2e8f0;
}

.config-code :deep(.json-key) {
  color: #93c5fd;
}

.config-code :deep(.json-string) {
  color: #86efac;
}

.config-code :deep(.json-number) {
  color: #fca5a5;
}

.config-code :deep(.json-boolean) {
  color: #f9a8d4;
}

.config-code :deep(.json-null) {
  color: #fde68a;
}

/* 滚动条样式 */
.config-code pre::-webkit-scrollbar {
  height: 8px;
}

.config-code pre::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

.config-code pre::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 4px;
}

.config-code pre::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

/* 移动端适配 */
.checkin-card {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-top: 20px;
}

.checkin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.checkin-header h3 {
  margin: 0;
  color: #333;
}

.checkin-summary {
  display: flex;
  align-items: center;
  gap: 16px;
}

.summary-item {
  color: #666;
  font-size: 14px;
}

.summary-item strong {
  color: #2196F3;
  font-size: 16px;
}

.checkin-body {
  display: flex;
  gap: 30px;
  align-items: flex-start;
}

.checkin-btn {
  padding: 8px 20px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  white-space: nowrap;
  flex-shrink: 0;
}

.checkin-btn:hover:not(:disabled) {
  background: #5a6fd6;
}

.checkin-btn:disabled {
  cursor: not-allowed;
}

.checkin-btn.checked {
  background: #4caf50;
}

.checkin-btn.loading {
  opacity: 0.8;
}

.checkin-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.checkin-calendar {
  flex: 1;
  min-width: 0;
}

.calendar-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.nav-btn {
  background: none;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 4px 10px;
  cursor: pointer;
  color: #666;
  transition: all 0.2s;
}

.nav-btn:hover {
  border-color: #2196F3;
  color: #2196F3;
}

.calendar-title {
  font-weight: 500;
  color: #333;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}

.calendar-weekday {
  text-align: center;
  font-size: 12px;
  color: #999;
  padding: 4px 0;
}

.calendar-cell {
  text-align: center;
  padding: 6px 2px;
  border-radius: 6px;
  font-size: 13px;
  position: relative;
  min-height: 36px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.calendar-cell.empty {
  visibility: hidden;
}

.calendar-cell.today {
  border: 2px solid #2196F3;
}

.calendar-cell.checked {
  background: #e8f5e9;
}

.calendar-cell.checked .cell-day {
  color: #2e7d32;
  font-weight: 600;
}

.cell-day {
  color: #333;
}

.cell-reward {
  font-size: 10px;
  color: #ff9800;
  font-weight: 600;
}

.checkin-message {
  margin-top: 15px;
  padding: 10px 15px;
  border-radius: 6px;
  font-size: 14px;
}

.checkin-message.success {
  background: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #c8e6c9;
}

.checkin-message.error {
  background: #ffebee;
  color: #c62828;
  border: 1px solid #ffcdd2;
}

@media (max-width: 768px) {
  .profile {
    padding: 10px;
  }

  .profile-main {
    flex-direction: column;
    gap: 20px;
  }
  
  .checkin-body {
    flex-direction: column;
  }
  
  .checkin-calendar {
    width: 100%;
  }
  
  .checkin-header {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }
  
  .checkin-summary {
    flex-wrap: wrap;
  }
  
  .profile-card {
    padding: 15px;
    border-radius: 12px;
  }

  .profile-ad {
    width: 100%;
    max-width: none;
  }

  .ad-carousel {
    aspect-ratio: 1444 / 527;
    border-radius: 12px;
  }

  .ad-nav {
    width: 32px;
    height: 32px;
  }
  
  .info-item {
    flex-direction: column;
    margin: 20px 0;
    gap: 8px;
  }
  
  .info-item label {
    width: auto;
    font-weight: 600;
    color: #333;
  }
  
  .api-container, .config-container {
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .buy-btn, .key-btn, .config-btn {
    padding: 10px 16px;
    font-size: 14px;
    min-width: 120px;
    justify-content: center;
  }
  
  .refresh-btn {
    padding: 10px 16px;
    font-size: 14px;
    width: 100%;
    margin-top: 10px;
  }
  
  /* 弹窗适配 */
  .modal {
    padding: 10px;
  }
  
  .modal-content {
    width: 95%;
    max-width: none;
    margin: 20px auto;
    max-height: 90vh;
    overflow-y: auto;
  }
  
  .config-modal {
    width: 98%;
  }
  
  .config-code pre {
    padding: 15px;
    font-size: 12px;
    overflow-x: auto;
  }
  
  .product-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .product-card {
    padding: 15px;
  }
  
  .key-input {
    padding: 14px 16px;
    font-size: 16px; /* 防止iOS缩放 */
  }
}

@media (max-width: 480px) {
  .profile {
    padding: 5px;
  }
  
  .profile-card {
    padding: 12px;
  }

  .ad-carousel {
    aspect-ratio: 1444 / 527;
  }

  .ad-nav {
    width: 28px;
    height: 28px;
  }

  .ad-nav-prev {
    left: 8px;
  }

  .ad-nav-next {
    right: 8px;
  }

  .ad-dots {
    margin-top: 10px;
  }
  
  .info-item {
    margin: 15px 0;
  }
  
  .buy-btn, .key-btn, .config-btn {
    padding: 8px 12px;
    font-size: 13px;
    min-width: 100px;
  }
  
  .modal-content {
    margin: 10px auto;
    padding: 15px;
  }
  
  .config-code pre {
    padding: 10px;
    font-size: 11px;
  }
  
  .product-card {
    padding: 12px;
  }
  
  .copy-btn {
    padding: 6px 12px;
    font-size: 13px;
  }
}

/* 触摸设备优化 */
@media (hover: none) and (pointer: coarse) {
  .buy-btn:hover, .key-btn:hover, .config-btn:hover {
    transform: none;
  }
  
  .buy-btn:active {
    background-color: #2E7D32;
    transform: scale(0.98);
  }
  
  .key-btn:active {
    background-color: #E65100;
    transform: scale(0.98);
  }
  
  .config-btn:active {
    background-color: #7B1FA2;
    transform: scale(0.98);
  }
  
  .key-input {
    -webkit-appearance: none;
    border-radius: 8px;
  }

  .tktoken-container{
    flex-direction: column;

  }
}
</style>
